import { useState } from 'react';
import Sheet from '../Sheet';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_PRIMARY, BTN_SECONDARY_ON_PAGE } from '../buttonStyles';
import { CARD, ROW_TEXT, ROW_VALUE, SECTION_FOOTNOTE, SECTION_HEADER } from './surfaces';
import { adjustLiveLessonTime } from '../../services/liveV2';
import { liveLessonsErrorMessage } from '../../services/liveLessons';
import { useToast } from '../../hooks/useToast';
import { whenLine } from '../../utils/liveCalendar';
import { firstNameOf, liveLessonTitle } from '../../utils/liveLessonText';
import type { LiveCallBooking } from '../../types/liveCall';

const STEPS = [5, 10, 15, 20, 30];

/**
 * The thread's "Next call" sheet (decision 166, changes.md 8), in place of the bottom "Move ... Lesson" capsule:
 * the lesson's title, its date and time and length; Pick a New Time, the primary, for the calendar picker (the
 * player's move is instant, 123; the coach's is a request, 165); and Adjust by a Few Minutes, an earlier or later
 * chip, then Send.
 */
export function NextCallSheet({ booking, isCoach, tz, canAdjust, onPickNewTime, onClose }: {
  booking: LiveCallBooking;
  isCoach: boolean;
  tz: string;
  /** Adjusting by minutes is open (the lesson's Move window). */
  canAdjust: boolean;
  onPickNewTime: () => void;
  onClose: () => void;
}) {
  const { showError } = useToast();
  const [minutes, setMinutes] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const other = firstNameOf(isCoach ? booking.playerName : booking.coachName, isCoach ? 'your player' : 'your coach');
  const title = booking.membershipId ? 'Monthly Coaching · Live Video Lesson' : liveLessonTitle(booking);
  const send = async () => {
    if (minutes === null) return;
    setSending(true);
    try {
      await adjustLiveLessonTime({ bookingId: booking.id, minutes });
      onClose();
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not move the lesson. Please try again.'));
    } finally {
      setSending(false);
    }
  };
  const chip = (m: number) => (
    <button key={m} type="button" onClick={() => setMinutes(minutes === m ? null : m)} aria-pressed={minutes === m} disabled={sending}
      aria-label={`${m < 0 ? 'Earlier' : 'Later'} by ${Math.abs(m)} minutes`} data-adjust-minutes={m}
      className={`ios-press h-11 flex-1 cursor-pointer rounded-[100px] text-[15px] leading-[20px] font-medium tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 disabled:opacity-40 ${
        minutes === m ? 'bg-[rgba(26,107,138,0.12)] text-biscayne ring-[1.5px] ring-inset ring-biscayne' : 'border border-[rgba(0,0,0,0.08)] bg-white text-db-text-1'}`}>
      {Math.abs(m)}
    </button>
  );
  return (
    <Sheet title={title} onClose={onClose} busy={sending}>
      <div data-next-call-sheet>
        <div className={`flex min-h-[52px] items-center gap-3 px-4 ${CARD}`}>
          <span className={`${ROW_TEXT} flex-1 tabular-nums`}>{whenLine(booking.scheduledStartAt, tz)}</span>
          <span className={`${ROW_VALUE} tabular-nums`}>{booking.durationMinutes ?? 45} min</span>
        </div>
        <button type="button" onClick={onPickNewTime} disabled={sending} className={`${BTN_PRIMARY} mt-6 w-full`} data-next-call-pick>
          Pick a New Time
        </button>
        {canAdjust && (<>
          <h3 className={`mt-8 ${SECTION_HEADER}`}>Adjust by a Few Minutes</h3>
          <p className="mb-2 text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]">Earlier</p>
          <div className="flex gap-2">{STEPS.map((m) => chip(-m))}</div>
          <p className="mb-2 mt-4 text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]">Later</p>
          <div className="flex gap-2">{STEPS.map((m) => chip(m))}</div>
          <p className={SECTION_FOOTNOTE}>{other} confirms with one tap.</p>
          <button type="button" onClick={() => { void send(); }} disabled={minutes === null || sending} aria-busy={sending}
            className={`${BTN_SECONDARY_ON_PAGE} mb-2 mt-4 w-full`} data-next-call-send>
            {sending ? <ButtonSpinner size={18} /> : 'Send'}
          </button>
        </>)}
      </div>
    </Sheet>
  );
}
